import './Game.scss';
import store, { RootState } from "../store";
import { gameActions } from './gameSlice';
import { useAppSelector } from '../redux';
import BattleScreen from './BattleScreen';
import StartScreen from '../StartScreen/StartScreen';
import { GamePlayState } from '../Enums/GameEnums'
import { GameOverScreen } from '../GameOver/GameOverScreen'
import { RewardScreen } from '../Reward/RewardScreen'
import CharacterSelectionScreen from '../StartScreen/CharacterSelectionScreen';
import Story from '../StartScreen/Story';
import MusicAsset from "../Components/MusicAsset";
import { useEffect, useState, useLayoutEffect } from 'react';
import Scoreboard from '../Scoreboard/Scoreboard';
import Help from '../Help/Help';
import ImageAsset from '../Components/ImageAsset';
import Credits from '../Credits/Credits';

const LANDSCAPE_BASE = 770;
const PORTRAIT_BASE = 730;
const SQUARE_BASE = 1100;

const SQUARE_THRESHOLD_PORTRAIT = 0.43;
const SQUARE_THRESHOLD_LANDSCAPE = 0.4;

export const ORIENTATION_SQUARE = 'square';
export const ORIENTATION_LANDSCAPE = 'landscape';
export const ORIENTATION_PORTRAIT = 'portrait';

function Game() {
  const gameState = useAppSelector((state: RootState) => state.game);

  const [dimensions, setDimensions] = useState({
    scale: 1,
    vWidth: LANDSCAPE_BASE,
    vHeight: LANDSCAPE_BASE,
    orientation: ORIENTATION_LANDSCAPE,
    currentBase: LANDSCAPE_BASE
  });

  useLayoutEffect(() => {
    function handleResize() {
      const { innerWidth: width, innerHeight: height } = window;
      const aspectRatio = width / height;

      let currentBase: number;
      let orientation: string;

      if (aspectRatio >= 1 - SQUARE_THRESHOLD_PORTRAIT && aspectRatio <= 1 + SQUARE_THRESHOLD_LANDSCAPE) {
        currentBase = SQUARE_BASE;
        orientation = ORIENTATION_SQUARE;
      } else if (width > height) {
        currentBase = LANDSCAPE_BASE;
        orientation = ORIENTATION_LANDSCAPE;
      } else {
        currentBase = PORTRAIT_BASE;
        orientation = ORIENTATION_PORTRAIT;
      }

      const scale = Math.min(width, height) / currentBase;
      const vWidth = width / scale;
      const vHeight = height / scale;

      setDimensions({
        scale,
        vWidth,
        vHeight,
        orientation,
        currentBase
      });
    }

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (
        e.key === 'Enter' || e.keyCode === 13 ||
        e.key === "Escape" || e.keyCode === 27
      ) {
        e.preventDefault();
        store.dispatch(gameActions.closePopup());
        store.dispatch(gameActions.hideScoreboard());
        store.dispatch(gameActions.hideHelp());
        store.dispatch(gameActions.hideCredits());
      }
    };

    document.addEventListener('keydown', keyDownHandler);
    return () => document.removeEventListener('keydown', keyDownHandler);
  }, []);

  function ScreenHandler() {
    if (gameState.isScoreboardVisible) return <Scoreboard />;
    if (gameState.isHelpVisible) return <Help />;
    if (gameState.isCreditsVisible) return <Credits />;

    switch (gameState.gamePlayState) {
      case GamePlayState.GAME_START: return <StartScreen orientation={dimensions.orientation} />;
      case GamePlayState.DEFEAT: return <GameOverScreen />;
      case GamePlayState.VICTORY: return <RewardScreen />;
      case GamePlayState.STORY_OUTRO:
      case GamePlayState.STORY_INTRO: return <Story />;
      case GamePlayState.CHARACTER_SELECTION: return <CharacterSelectionScreen />;
      case GamePlayState.BETTING:
      case GamePlayState.PLAYER_CARD_REVEAL:
      case GamePlayState.PLAYER_ACTION:
      case GamePlayState.DEALER_CARD_REVEAL: return <BattleScreen />;
      default: return null;
    }
  }

  function muteClick() {
    store.dispatch(gameActions.setMute(!gameState.isMuted));
  }

  return (
    <div className="game-wrapper">
      <div
        className={`game ${dimensions.orientation}`}
        style={{
          transform: `scale(${dimensions.scale})`,
          width: `${dimensions.vWidth}px`,
          height: `${dimensions.vHeight}px`,
          position: 'relative',
          transformOrigin: 'center center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >

        <ImageAsset
          classes='mute-button'
          onClick={muteClick}
          name={gameState.isMuted ? 'sound-off.png' : 'sound-on.png'}
          multiplier={3}
          showPointerOnHover={true}
        />

        {gameState.gamePlayState === GamePlayState.GAME_START && gameState.hasGameLoaded && <MusicAsset name="BlackjackRPG_Title Music No Loop_20.10.2023.mp3" loop={true} />}
        {gameState.gamePlayState === GamePlayState.DEFEAT && <MusicAsset name="BlackjackRPG_Somber Music_20.10.2023.mp3" loop={true} />}
        {gameState.gamePlayState === GamePlayState.VICTORY && <MusicAsset name="BlackjackRPG_Victory Fanfare_20.10.2023.mp3" />}
        {(gameState.gamePlayState === GamePlayState.STORY_INTRO ||
          gameState.gamePlayState === GamePlayState.CHARACTER_SELECTION ||
          gameState.gamePlayState === GamePlayState.STORY_OUTRO) &&
          <MusicAsset name="BlackjackRPG_Storytelling Music_20.10.2023.mp3" loop={true} />
        }
        {gameState.gamePlayState === GamePlayState.BETTING && <MusicAsset name="BlackjackRPG_Lobby Music_20.10.2023.mp3" loop={true} />}
        {(gameState.gamePlayState === GamePlayState.PLAYER_CARD_REVEAL ||
          gameState.gamePlayState === GamePlayState.PLAYER_ACTION ||
          gameState.gamePlayState === GamePlayState.DEALER_CARD_REVEAL) &&
          <MusicAsset name="BlackjackRPG_Combat Music_20.10.2023.mp3" loop={true} />
        }

        {ScreenHandler()}
      </div>
    </div>
  );
}

export default Game;